import React, { useEffect, useMemo, useState } from 'react';
import { BUTTON_GHOST, BUTTON_SOLID, FIELD, OPTION, SELECT } from './adminStyles';
import { fetchCallSheetTargets, updateCallSheetMeta, type CallSheetPayload, type CallSheetTargets } from '../../utils/callSheets';

/**
 * Editing a call sheet's identity — what day it is, what it's called, and
 * what it's attached to (13 Sep 2026).
 *
 * The gap this fills: the restructure that made call sheets standalone
 * backfilled every existing sheet's title from its project, so ATM's tech
 * test was called "ATM TV LIVE" and there was no way to correct it short of
 * deleting the sheet and building it again. A thing you can create and
 * destroy but not correct is a trap.
 *
 * Same fields and same narrowing rules as the create form, deliberately —
 * two differently-shaped versions of one form is how they drift.
 *
 * Detaching a project is the one change with a consequence beyond the label,
 * so the panel says it out loud rather than letting the crew list quietly
 * empty out after saving.
 */
export function CallSheetDetails({
  sheet,
  onCancel,
  onSaved
}: {
  sheet: CallSheetPayload;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [targets, setTargets] = useState<CallSheetTargets>({
    clients: [],
    shows: [],
    projects: []
  });
  const [form, setForm] = useState({
    clientName: sheet.clientName,
    showId: sheet.showId ?? '',
    projectId: sheet.projectId ?? '',
    title: sheet.title ?? '',
    shootDate: sheet.shootDate ?? ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    void (async () => setTargets(await fetchCallSheetTargets()))();
  }, []);
  const shows = useMemo(() => targets.shows.filter(show => !form.clientName || show.clientName === form.clientName), [targets.shows, form.clientName]);
  const projects = useMemo(() => targets.projects.filter(project => (!form.clientName || project.clientName === form.clientName) && (!form.showId || project.showId === form.showId)), [targets.projects, form.clientName, form.showId]);
  function edit(patch: Partial<typeof form>) {
    setForm(current => {
      const next = {
        ...current,
        ...patch
      };
      if (patch.clientName !== undefined) {
        next.showId = '';
        next.projectId = '';
      }
      if (patch.showId !== undefined) next.projectId = '';
      return next;
    });
  }
  const losingProject = Boolean(sheet.projectId) && form.projectId !== sheet.projectId;
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError('');
    if (!form.clientName.trim()) {
      setError('Choose a client — every call sheet belongs to one.');
      return;
    }
    setSaving(true);
    const result = await updateCallSheetMeta(sheet.id, {
      clientName: form.clientName.trim(),
      title: form.title.trim(),
      shootDate: form.shootDate,
      showId: form.showId || null,
      projectId: form.projectId || null
    });
    setSaving(false);
    if (!result.ok) {
      setError(result.error ?? 'The details could not be saved.');
      return;
    }
    onSaved();
  }
  return <form onSubmit={submit} className="mt-8 border border-paper/25 px-6 py-7 sm:px-8" data-id="element-644" data-mp="2h5pd6c11awb1">
      <div className="grid grid-cols-1 gap-7 sm:grid-cols-2" data-id="element-645" data-mp="2m51ygsc9bvbi">
        <label className="block sm:col-span-2" data-id="element-646" data-mp="2m6lfil5qmaec">
          <span className="label block text-paper/40" data-id="element-647" data-mp="2r4wpcvw18kwk">What is this day?</span>
          <input value={form.title} onChange={event => edit({
          title: event.target.value
        })} placeholder="e.g. Live — Monday, Tech test, Build day" className={FIELD} data-id="element-648" data-mp="2w3ho0qvmywiw" />
        </label>

        <label className="block" data-id="element-649" data-mp="34uqfrin6rd8e">
          <span className="label block text-paper/40" data-id="element-650" data-mp="34xw0ymsp1uyu">Date</span>
          <input type="date" value={form.shootDate} onChange={event => edit({
          shootDate: event.target.value
        })} className={`${FIELD} [color-scheme:dark]`} data-id="element-651" data-mp="39w7cd1yn0dj6" />
        </label>

        <label className="block" data-id="element-652" data-mp="3155pxqel0qw0">
          <span className="label block text-paper/40" data-id="element-653" data-mp="2w8p4x52oisn5">Client</span>
          <select value={form.clientName} onChange={event => edit({
          clientName: event.target.value
        })} className={SELECT} data-id="element-654" data-mp="2w62v0ysbg11r">
            <option value="" className={OPTION} data-id="element-655" data-mp="35yh3099zm3wd">
              Choose a client…
            </option>
            {targets.clients.map(client => <option key={client} value={client} className={OPTION} data-id="element-656" data-mp="2d9jl479ou945">
                {client}
              </option>)}
          </select>
        </label>

        <label className="block" data-id="element-657" data-mp="2s4hcoxx0iuk6">
          <span className="label block text-paper/40" data-id="element-658" data-mp="2n7qx0bp4e0za">Show (optional)</span>
          <select value={form.showId} onChange={event => edit({
          showId: event.target.value
        })} disabled={shows.length === 0} className={`${SELECT} disabled:opacity-40`} data-id="element-659" data-mp="2n9twrng2r8h7">
            <option value="" className={OPTION} data-id="element-660" data-mp="a0845tyt6tkh">
              — none —
            </option>
            {shows.map(show => <option key={show.id} value={show.id} className={OPTION} data-id="element-661" data-mp="f0nj2pyqo94k">
                {show.name}
              </option>)}
          </select>
        </label>

        <label className="block" data-id="element-662" data-mp="50wkac35b7yo">
          <span className="label block text-paper/40" data-id="element-663" data-mp="526jx5pnco7f">Project (optional)</span>
          <select value={form.projectId} onChange={event => edit({
          projectId: event.target.value
        })} disabled={projects.length === 0} className={`${SELECT} disabled:opacity-40`} data-id="element-664" data-mp="ssadsld199l8">
            <option value="" className={OPTION} data-id="element-665" data-mp="jwul47o0l0h8">
              — none —
            </option>
            {projects.map(project => <option key={project.id} value={project.id} className={OPTION} data-id="element-666" data-mp="1bplaby7a5owz">
                {project.name}
              </option>)}
          </select>
        </label>
      </div>

      {losingProject && <p className="label-sm mt-6 max-w-xl leading-relaxed text-paper/50" data-id="element-667" data-mp="1qbshhehzq80q">
          Changing the project changes who’s on this call sheet: crew inherited from{' '}
          {sheet.projectName ?? 'the old project'} drop off, and the client contact goes with them.
          Anyone you added to this day directly stays put.
        </p>}

      {error && <p role="alert" className="mt-6 label-sm text-paper/70" data-id="element-668" data-mp="17dpluguxtcty">
          {error}
        </p>}

      <div className="mt-7 flex flex-wrap items-center gap-5" data-id="element-669" data-mp="12nhkwnjbuui9">
        <button type="submit" disabled={saving} className={BUTTON_SOLID} data-id="element-670" data-mp="12l4udlzi7btl">
          {saving ? 'Saving…' : 'Save Details'}
        </button>
        <button type="button" onClick={onCancel} className={BUTTON_GHOST} data-id="element-671" data-mp="tn2ytlyjhbdp">
          Cancel
        </button>
      </div>
    </form>;
}